package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.lex.TokenType

/**
 * 解析 screen 级 `open { ... }` / `close { ... }` 生命周期块（Stage 1）。
 *
 * - 当前 token 必须是软关键字 IDENTIFIER `open` 或 `close`。
 * - 不允许形参；不允许返回值类型标注。
 * - body 复用 [ActionBlockParser.parseLambdaBody]，与 fun body 共用同一套语法
 *   （var / if / while / fun 局部声明 / state 赋值 / 表达式语句等全部支持）。
 *
 * suspend 与同步限制由后续阶段处理：
 * - open body 天然 suspend，由 [SuspendKeywordChecker] / 语义层负责 await 合法性；
 * - close body 的同步限制由 [top.wcpe.allincore.dsl.compiler.semantic.CloseBlockChecker]
 *   在编译期拒绝 await / delay / net.request / state 写入 / local.* / suspend fun 调用。
 */
internal class OpenCloseBlockParser(private val p: Parser) {

    /** 解析 `open { ... }`，返回 stmt 列表；调用方负责消费 `open` 之前的位置约束。 */
    fun parseOpen(): List<StmtAst> = parseBlock("open")

    /** 解析 `close { ... }`，返回 stmt 列表；调用方负责消费 `close` 之前的位置约束。 */
    fun parseClose(): List<StmtAst> = parseBlock("close")

    /** 解析 `show { ... }`（每次显示触发，语义同 open）。 */
    fun parseShow(): List<StmtAst> = parseBlock("show")

    /** 解析 `onHide { ... }`（每次隐藏触发，语义同 open）。 */
    fun parseHide(): List<StmtAst> = parseBlock("onHide")

    private fun parseBlock(keyword: String): List<StmtAst> {
        val keywordTok = p.current()
        check(keywordTok.type == TokenType.IDENTIFIER && keywordTok.lexeme == keyword) {
            "OpenCloseBlockParser 入口 token 必须为 '$keyword'，实际: ${keywordTok.lexeme}"
        }
        p.advance() // 消费 open / close 软关键字
        p.skipNewlines()
        // 不允许形参：直接要求 `{`，不接受 `(` 形参列表
        if (p.check(TokenType.LPAREN)) {
            p.fail("$keyword 块不接受形参列表", p.current().position)
        }
        p.expect(TokenType.LBRACE, "$keyword 头部后缺少 '{'")
        p.skipNewlines()
        val body = ActionBlockParser(p).parseLambdaBody(params = emptyList(), allowBareIdent = false)
        p.expect(TokenType.RBRACE, "$keyword 块缺少 '}'")
        return body
    }
}
